import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  PenLine, BookOpen, MessageCircle, UserPlus, Users,
  Trophy, Flame, Star, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const ICON_MAP = {
  'pen-line': PenLine,
  'book-open': BookOpen,
  'library': BookOpen,
  'message-circle': MessageCircle,
  'star': Star,
  'user-plus': UserPlus,
  'users': Users,
  'trophy': Trophy,
  'flame': Flame,
};

const AchievementsPage = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const res = await api.get('/achievements');
        setData(res.data);
      } catch (e) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };
    fetchAchievements();
  }, [api]);

  if (loading || !data) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="achievements-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-text-primary">Achievements</h1>
          <p className="text-text-secondary mt-1">{data.earned_count} of {data.total} earned</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-brand-accent">{data.earned_count}</div>
          <div className="text-xs text-text-muted uppercase tracking-wider">Badges</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-3 bg-surface-alt rounded-full mb-10 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-brand-primary to-brand-accent rounded-full transition-all duration-700"
          style={{ width: `${(data.earned_count / data.total) * 100}%` }}
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="achievements-grid">
        {data.achievements.map(ach => {
          const Icon = ICON_MAP[ach.icon] || Star;
          return (
            <Card
              key={ach.key}
              className={`border transition-all ${
                ach.earned
                  ? 'border-brand-accent/30 bg-white'
                  : 'border-[#E5E2DC] bg-surface-alt/50 opacity-60'
              }`}
              data-testid={`achievement-${ach.key}`}
            >
              <CardContent className="p-5 flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  ach.earned ? 'bg-brand-accent/15' : 'bg-surface-alt'
                }`}>
                  {ach.earned ? (
                    <Icon className="w-6 h-6 text-brand-accent" />
                  ) : (
                    <Lock className="w-5 h-5 text-text-muted" />
                  )}
                </div>
                <div>
                  <h3 className={`font-medium text-sm ${ach.earned ? 'text-text-primary' : 'text-text-muted'}`}>
                    {ach.name}
                  </h3>
                  <p className="text-xs text-text-muted mt-0.5">{ach.description}</p>
                  {ach.earned && (
                    <span className="inline-block mt-2 text-[10px] font-medium text-brand-accent uppercase tracking-wider">Earned</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AchievementsPage;
